import { useState, useCallback, useMemo } from "react";
import { useRouter } from "next/router";
import { useAtomValue, useAtom, useSetAtom } from "jotai";
import { Box, TextField, Button, Dialog, DialogContent, DialogActions, Typography } from "@mui/material";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import { themeState, textColorState, greyDarkerState, backgroundColorState, darkerBackgroundColorState, chatModalOpenState, chatMinimizedState, chatModalState, userState, ortexColorState } from "@/lib/store";
import { classNames } from "@/lib/utils";
import styles from "./ChatInput.module.scss";

interface ChatInputProps {
    placeholder: string;
    startingModel: string;
}

const ChatInput = ({ placeholder, startingModel }: ChatInputProps) => {
    const router = useRouter();
    const theme = useAtomValue(themeState);
    const textColor = useAtomValue(textColorState);
    const greyDarker = useAtomValue(greyDarkerState);
    const backgroundColor = useAtomValue(backgroundColorState);
    const darkerBackgroundColor = useAtomValue(darkerBackgroundColorState);
    const ortexColor = useAtomValue(ortexColorState);
    const user = useAtomValue(userState);

    // Only subscribe to the 'open' property via selector to prevent rerenders when other properties change
    const chatModalOpen = useAtomValue(chatModalOpenState);
    const [isChatMinimized, setIsChatMinimized] = useAtom(chatMinimizedState);
    const setChatModal = useSetAtom(chatModalState);

    const [message, setMessage] = useState("");
    const [loginRequiredOpen, setLoginRequiredOpen] = useState(false);

    const isVisitor = user?.subscription_plan?.includes("Visitor");

    const handleSendMessage = useCallback(() => {
        if (!message.trim()) return;
        if (isVisitor) {
            setLoginRequiredOpen(true);
            return;
        }
        setIsChatMinimized(false);
        setChatModal((prev) => ({ ...prev, open: true, initialMessage: message, startingModel, chatModel: undefined }));
        setMessage("");
    }, [message, isVisitor, setIsChatMinimized, setChatModal, startingModel]);

    const handleKeyPress = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                handleSendMessage();
            }
        },
        [handleSendMessage]
    );

    const isDisabled = useMemo(() => chatModalOpen && !isChatMinimized, [chatModalOpen, isChatMinimized]);

    const handleGoToLogin = useCallback(() => {
        setLoginRequiredOpen(false);
        router.push("/login");
    }, [router]);

    return (
        <Box
            className={classNames(styles.container, isDisabled && styles.disabled)}
            sx={{
                backgroundColor: theme === "dark" ? backgroundColor : darkerBackgroundColor,
            }}
        >
            <TextField
                fullWidth
                placeholder={placeholder}
                variant="outlined"
                size="small"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleKeyPress}
                multiline
                maxRows={2}
                disabled={isDisabled}
                className={styles.textField}
                sx={{
                    "& .MuiOutlinedInput-root": {
                        color: textColor,
                        "& fieldset": {
                            borderColor: theme === "dark" ? greyDarker : undefined,
                        },
                        "&:hover fieldset": {
                            borderColor: theme === "dark" ? greyDarker : undefined,
                        },
                    },
                }}
            />
            <Button
                variant="contained"
                onClick={handleSendMessage}
                disabled={!message.trim()}
                className={styles.sendButton}
                sx={{
                    color: textColor,
                    bgcolor: `${ortexColor} !important`,
                    // The button dims while empty (ChatInput.module.scss); keep its label color.
                    "&.Mui-disabled": { color: "primary.contrastText" },
                }}
            >
                Send
            </Button>

            <Dialog
                open={loginRequiredOpen}
                onClose={() => setLoginRequiredOpen(false)}
                slotProps={{
                    backdrop: { className: styles.loginDialogBackdrop },

                    paper: {
                        className: classNames(styles.loginDialogPaper, theme === "dark" && styles.dark),
                    },
                }}
            >
                <DialogContent className={styles.loginDialogContent} sx={{ color: textColor }}>
                    <Box className={styles.loginDialogIconWrap} sx={{ "& .MuiSvgIcon-root": { color: ortexColor } }}>
                        <ChatBubbleOutlineRoundedIcon />
                    </Box>
                    <Typography variant="h6" className={styles.loginDialogTitle}>
                        Sign in to chat
                    </Typography>
                    <Typography variant="body2" className={styles.loginDialogBody}>
                        Create a free account or log in to chat with the ORTEX assistant and get answers instantly.
                    </Typography>
                </DialogContent>
                <DialogActions className={styles.loginDialogActions}>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleGoToLogin}
                        className={styles.loginDialogPrimaryBtn}
                        sx={{ bgcolor: `${ortexColor} !important`, color: "#fff", "&:hover": { bgcolor: `${ortexColor} !important`, filter: "brightness(1.1)" } }}
                    >
                        Sign in
                    </Button>
                    <Button variant="text" onClick={() => setLoginRequiredOpen(false)} className={styles.loginDialogSecondaryBtn} sx={{ color: textColor }}>
                        Not now
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ChatInput;
